import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import AgentInfo from '../AgentInfo/AgentInfo';
import WhatsAppButton from '../WhatsAppButton/WhatsAppButton';
import { createWhatsAppLink } from '../../utils';
import { PropertyDetailFragment } from '../../../gatsby-graphql';

interface IPropertyDetailContactProps {
  parentElement: HTMLDivElement | null;
  locationHref: string;
  agent: PropertyDetailFragment['propertyAgent'];
}

const PropertyDetailContact: React.FunctionComponent<IPropertyDetailContactProps> = ({
  parentElement,
  agent,
  locationHref,
}) => {
  const [topPosition, setTopPosition] = useState<number>(0);
  const containerElement = useRef<HTMLDivElement | null>(null);
  const contentElement = useRef<HTMLDivElement | null>(null);

  const handleDesktopScroll = (
    _container: HTMLDivElement,
    _content: HTMLDivElement,
    _parent: HTMLDivElement
  ) => {
    const { scrollY } = window;
    const { offsetTop } = _container;
    const { offsetHeight } = _content;
    const parentOffsetBottom = _parent.offsetTop + _parent.offsetHeight;
    const topOffset = 50;
    const stickyOffset = offsetTop - topOffset;
    const contentOffsetBottom = scrollY + topOffset + offsetHeight;
    if (scrollY >= stickyOffset && contentOffsetBottom <= parentOffsetBottom) {
      setTopPosition(scrollY - stickyOffset);
    } else if (contentOffsetBottom < parentOffsetBottom) {
      setTopPosition(0);
    }
  };

  const handleScroll = () => {
    if (!containerElement.current || !contentElement.current || !parentElement)
      return;
    handleDesktopScroll(
      containerElement.current,
      contentElement.current,
      parentElement
    );
  };

  useEffect(() => {
    if (parentElement) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [parentElement]);

  const whatsAppText = `Hola ${agent?.name}, me gustaría conocer más sobre esta propiedad: ${locationHref}`;

  return (
    <Placeholder ref={containerElement}>
      <div style={{ top: topPosition }} ref={contentElement}>
        <Container>
          <span>Agente</span>
          <AgentInfo {...agent} />
        </Container>
        <Container>
          <span>Me interesa esta propiedad</span>
          <WhatsAppButton
            target="_blank"
            href={createWhatsAppLink(agent?.phone as string, whatsAppText)}
          >
            Quiero que me contacten
          </WhatsAppButton>
        </Container>
      </div>
    </Placeholder>
  );
};

const Placeholder = styled.aside`
  position: relative;
  margin-top: 40px;
  ${(props) => props.theme.mq.desktop} {
    margin-top: 0;
    & > div {
      position: absolute;
      left: 0;
      width: 100%;
    }
  }
`;

const Container = styled.div`
  border-radius: 8px;
  box-shadow: 0 2px 10px 2px rgba(0, 0, 0, 0.1);
  background-color: ${(props) => props.theme.colors.lightSurface};
  padding: 24px 20px 32px;
  & > span {
    display: block;
    font-size: 18px;
    line-height: 13px;
    margin: 10px 0 20px;
    color: ${(props) => props.theme.colors.dark.primary};
    font-weight: bold;
  }
  & + & {
    margin-top: 32px;
  }
`;

export default PropertyDetailContact;
